import queryString from 'query-string'

import { apiErrorMessage, apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { EXPORT_STATUS } from '@/utils/constants'

import * as redux from './callAssetExports.redux'

const BASE = () => `${apiService.reporting}/api/calls/assets/exports`

// Terminal states. Polling stops here rather than on a success check, so a
// state nobody anticipated leaves the page polling instead of silently
// claiming the export finished.
export const TERMINAL_STATUSES = [
  EXPORT_STATUS.succeeded,
  EXPORT_STATUS.partial,
  EXPORT_STATUS.failed,
  EXPORT_STATUS.expired,
]

export const isTerminal = (status) => TERMINAL_STATUSES.includes(status)

/**
 * What the selection would produce, without committing to it.
 *
 * Call count and total duration are exact. Size is an estimate from duration
 * and is labelled as one everywhere it appears: the real figure needs a HEAD
 * sweep over every object, which is far too slow to run while a modal waits.
 * It lands on the job a few seconds after the export starts.
 */
export const previewAssetExport =
  ({ callIds, assetTypes, organizationId, signal }) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ preview: true }))
    try {
      const preview = await fetchingAPI(
        `${BASE()}/preview`,
        'POST',
        dispatch,
        JSON.stringify({
          call_ids: callIds,
          asset_types: assetTypes,
          organization_id: organizationId,
        }),
        undefined,
        // isSilent: handleResponse toasts on a 500 and on anything it does not
        // recognise, which would fire on every checkbox ticked while reporting
        // is down. The modal renders its own inline error instead.
        { isSilent: true },
        undefined,
        signal
      )
      dispatch(redux.setPreview(preview))
      return preview
    } catch (err) {
      // An abort means a newer preview replaced this one, so neither the error
      // state nor the loading flag belongs to it any more -- claiming either
      // would let a superseded request overwrite the current answer.
      if (err?.name === 'AbortError' || signal?.aborted) {
        return null
      }
      // Quiet on purpose: the modal renders its own inline state. A toast here
      // would fire on every checkbox ticked while the network is slow. The
      // error flag is what stops it sitting on "working it out" forever.
      dispatch(redux.setPreviewError())
      return null
    } finally {
      if (!signal?.aborted) {
        dispatch(redux.setLoading({ preview: false }))
      }
    }
  }

/**
 * Create the export job and start its flow. Resolves to the job, or null.
 *
 * Failure is surfaced loudly here, unlike the preview: the user pressed a
 * button and is owed an answer. A job that was never created is worse than one
 * that fails later, because nothing will notify them about it.
 */
export const createAssetExport =
  ({ callIds, assetTypes, organizationId }) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ create: true }))
    try {
      const job = await fetchingAPI(
        BASE(),
        'POST',
        dispatch,
        JSON.stringify({
          call_ids: callIds,
          asset_types: assetTypes,
          organization_id: organizationId,
        })
      )
      dispatch(redux.setExport(job))
      return job
    } catch (err) {
      // A 409 is rejected with the raw body, so the wording that says which
      // limit was hit lives on `detail` rather than `message`. Only when it is
      // a string: FastAPI's 422 puts a list of objects there, which React
      // refuses to render and which would crash the page instead of the toast.
      toast.error(
        typeof err?.detail === 'string'
          ? err.detail
          : apiErrorMessage(err, 'Could not start the export. Please try again.')
      )
      return null
    } finally {
      dispatch(redux.setLoading({ create: false }))
    }
  }

/**
 * Current state of one export. Used by the job page, and polled while it runs.
 *
 * `quiet` suppresses both the spinner and the error toast so a failed poll on
 * an otherwise healthy page does not flash at the user -- the next tick will
 * most likely succeed, and the page keeps showing the last good state.
 */
export const fetchAssetExport =
  (exportId, { quiet = false } = {}) =>
  async (dispatch, getState) => {
    if (!quiet) {
      dispatch(redux.setLoading({ currentExport: true }))
      dispatch(redux.setExportNotFound(null))
      dispatch(redux.setExportForbidden(null))
    }
    try {
      // isSilent on a background poll: handleResponse toasts 5xx itself, so
      // without it a reporting-service outage toasts every tick while the page
      // is open.
      const job = await fetchingAPI(
        `${BASE()}/${exportId}`,
        'GET',
        dispatch,
        undefined,
        undefined,
        { isSilent: quiet }
      )
      dispatch(redux.setExport(job))
      return job
    } catch (err) {
      // A stale notification link, an export that has aged out, or one
      // belonging to another organization -- all answered 404 alike. An
      // expected-empty navigation, so it drives the empty state, not a toast.
      // A refusal is terminal for this viewer: the row exists and will not
      // start being theirs to read. Without this the page sat on its loader
      // for ever, re-asking on every poll, which reads as a hang rather than
      // a refusal. The message is the API's own -- handleResponse already
      // surfaces `detail`, and the backend says only "User not authorized"
      // for every reason it refuses, so inventing a more specific one here
      // would be wrong as often as right.
      if (err?.status === 403) {
        dispatch(redux.setExportForbidden(exportId))
        if (!quiet) {
          toast.error(apiErrorMessage(err, 'You cannot open this export.'))
        }
        return null
      }
      if (err?.status === 404) {
        const { currentExport } = getState().callAssetExports
        const haveThisOne = currentExport && String(currentExport.id) === String(exportId)
        // A background poll that 404s against a page already showing this
        // export is a blip -- a replica that has not caught up -- and must not
        // swap a working job page for the empty state. But when nothing has
        // ever loaded, suppressing it leaves the loader spinning and the poll
        // asking every five seconds for an export that does not exist.
        if (!quiet || !haveThisOne) {
          dispatch(redux.setExportNotFound(exportId))
        }
        return null
      }
      if (!quiet) {
        toast.error('Could not load this export.', { cause: err })
      }
      return null
    } finally {
      if (!quiet) {
        dispatch(redux.setLoading({ currentExport: false }))
      }
    }
  }

/**
 * Start the download.
 *
 * Two steps, and the split is forced rather than chosen: fetchingAPI
 * authenticates through custom headers (token, username, organizationid), and
 * a plain navigation carries none of them -- so a 302 from the endpoint could
 * never be authorized. The URL is fetched through fetchingAPI instead, which
 * does authorize, and only then is the browser sent to it.
 *
 * It lands in a better place anyway. The presigned URL is minted at click time
 * and points straight at S3, so the archive never travels through the service
 * and the browser gets a real download with its own progress, rather than
 * gigabytes pulled through the tab.
 */
export const downloadAssetExport = (exportId) => async (dispatch) => {
  try {
    const { url } = await fetchingAPI(`${BASE()}/${exportId}/download`, 'GET', dispatch)
    if (!url) {
      throw new Error('no download url')
    }
    window.location.assign(url)
    return url
  } catch (err) {
    toast.error('Could not start the download. It may have expired.', { cause: err })
    return null
  }
}

/**
 * Recent exports for the organization.
 *
 * Backs the list page, which exists because notifications get dismissed and
 * emails get deleted -- without it, an export whose link you lost is
 * unreachable, and a colleague's is unreachable by design.
 */
export const fetchAssetExports =
  (organizationId, { quiet = false } = {}) =>
  async (dispatch, getState) => {
    if (!quiet) {
      dispatch(redux.setLoading({ exports: true }))
      dispatch(redux.setExportsForbidden(null))
      dispatch(redux.setExportsOrganization(organizationId))
    }
    // A load fired for one organization can resolve after the user has
    // switched to another, and nothing cancels a poll already in flight.
    // Without this the late answer lands under the new organization's
    // heading -- or, if it failed, empties a list that belongs to an
    // organization it was never about.
    const isStale = () => getState().callAssetExports.exportsOrganizationId !== organizationId
    try {
      // isSilent on a background poll: handleResponse toasts 5xx itself, so
      // without it a reporting outage toasts on every tick while the page is
      // open.
      const exports = await fetchingAPI(
        `${BASE()}?${queryString.stringify({ organization_id: organizationId })}`,
        'GET',
        dispatch,
        undefined,
        undefined,
        { isSilent: quiet }
      )
      if (isStale()) {
        return null
      }
      dispatch(redux.setExports(exports || []))
      return exports
    } catch (err) {
      if (isStale()) {
        return null
      }
      // A refusal is terminal for this organization, not a blip. Job
      // metadata is no longer withheld from Balto for an unredacted
      // organization, so reaching this means no access to the organization
      // at all -- and without it the previous organization's rows stayed on
      // screen under a heading naming the new one.
      if (err?.status === 403) {
        dispatch(redux.setExports([]))
        dispatch(redux.setExportsForbidden(organizationId))
        return null
      }
      // A failed poll leaves the last good list on screen; the next tick most
      // likely succeeds, and a toast per tick would be worse than silence. A
      // deliberate load is different -- the rows on screen belong to whatever
      // was asked for last, which may be another organization entirely.
      if (!quiet) {
        dispatch(redux.setExports([]))
        toast.error('Could not load your exports.', { cause: err })
      }
      return null
    } finally {
      if (!quiet) {
        dispatch(redux.setLoading({ exports: false }))
      }
    }
  }
